import { SurveyEventSubmit, SurveyEventEmailTemplateEnum, SurveyEventStatusEnum } from "../models/survey.event.model";
import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";
import { titleStr } from "../lib/helper";
import { FilterGroup, FilterType } from "../components/TableFilter";
import { HeadCell, Pagination, PaginationFilterType } from "../models/base.model";
import {
  deleteSurveyEventSubmit,
  getAllRecordsUrl,
  getSubmitAnswers,
  getSurveyEventSubmitsById,
  sendSurveyEventEmails,
} from "../actions/survey.event.action";
import { Customer } from "../models/customer.model";
import { getCustomers } from "../actions/customer.action";
import { Box, Chip, Stack, TextField, Typography } from "@mui/material";
import TooltipForForm from "../components/TooltipForForm";
import { getItemsNode } from "../components/EllipsisText";
import moment from "moment";
import LoadingButton from "../components/LoadingButton";
import SurveySubmitAnswerView from "./SurveySubmitAnswerView";
import { getCompanyStoresByCompanyId } from "../actions/company.store.action";
import { CompanyStore } from "../models/company.store.model";
import PaginatedTableList from "../components/PaginatedTableList";
import { openWindowInAsync } from "../actions/common.action";

const status_type_color_map: { [key in SurveyEventStatusEnum]: any } = {
  draft: "default",
  sent: "warning",
  viewed: "info",
  answered: "success",
};

const headCells: readonly HeadCell[] = [
  {
    id: "id",
    label: "Submit ID",
  },
  {
    id: "uid",
    label: "Customer ID",
  },
  {
    id: "name",
    label: "Customer Name",
  },
  {
    id: "device",
    label: "Device Info",
    render: (v) => {
      let device;
      try {
        if (v) {
          device = JSON.parse(v);
        }
      } catch (error) {
        console.warn("parse device fail", error);
      }
      return (
        <TooltipForForm title={getItemsNode(v)}>
          <span>{device?.vendor}</span>
        </TooltipForForm>
      );
    },
  },
  {
    id: "status",
    label: "Status",
    render: (v: SurveyEventStatusEnum) => <Chip color={status_type_color_map[v]} label={titleStr(v)} data-testid={titleStr(v)} />,
  },
  {
    id: "date",
    label: "Date",
    render: (_, record) => (
      <TooltipForForm
        title={
          <Stack gap={1}>
            <span>Viewed At: {record.viewedAt ? moment(record.viewedAt).format("YYYY-MM-DD hh:mm:ss") : "-"}</span>
            <span>Answered At: {record.answeredAt ? moment(record.answeredAt).format("YYYY-MM-DD hh:mm:ss") : "-"}</span>
            <span>Sent At: {record.sentAt ? moment(record.sentAt).format("YYYY-MM-DD hh:mm:ss") : "-"}</span>
          </Stack>
        }
      >
        <span>
          {record.answeredAt
            ? `Answered At: ${moment(record.answeredAt).format("YYYY-MM-DD")}`
            : record.viewedAt
            ? `Viewed At: ${moment(record.viewedAt).format("YYYY-MM-DD")}`
            : ""}
        </span>
      </TooltipForForm>
    ),
  },
  {
    id: "invalid",
    label: "Invalid",
    render: (_, record) => {
      if (record.invalid && record.invalidReason) {
        return (
          <TooltipForForm
            title={
              <Stack px={2} py={1} gap={1} component="ul" sx={{ listStyle: "decimal" }}>
                <li>{record.invalidReason}</li>
              </Stack>
            }
          >
            <Chip color="error" label="Invalid" data-testid={"Invalid"} />
          </TooltipForForm>
        );
      } else if (record?.invalid === false) {
        return <Chip color="success" label="Valid" data-testid={"Valid"} />;
      }
      return <div></div>;
    },
  },
  {
    id: "answers",
    label: "Answers",
    render: (_, submit) => (
      <LoadingButton
        variant="contained"
        onClick={async () => {
          const { answers, questions } = await getSubmitAnswers({
            eventId: submit.surveyEventId,
            submitId: submit.id,
          });
          await window.webApp.toggleDialog({
            title: `Survey Answer`,
            element: (
              <SurveySubmitAnswerView questions={questions} answers={answers} store={cached_stores?.get(submit.storeId)} />
            ),
            minWidth: 800,
            onOk: async () => {},
            okText: "Close",
            noCancel: true,
            open: true,
          });
        }}
      >
        View
      </LoadingButton>
    ),
  },
];

const filters: FilterGroup<SurveyEventSubmit>[] = [
  {
    title: "Filter By Date",
    items: [
      {
        name: "viewedAt",
        type: FilterType.date_range,
      },
      {
        name: "sentAt",
        type: FilterType.date_range,
      },
      {
        name: "answeredAt",
        type: FilterType.date_range,
      },
    ],
  },
  {
    title: "Filter By Status",
    items: [
      {
        name: "status",
        type: FilterType.selection,
        options: Object.values(SurveyEventStatusEnum).map((status) => ({
          label: titleStr(status),
          value: status,
        })),
        multiple: true,
      },
      {
        name: "invalid",
        type: FilterType.selection,
        options: [
          {
            label: "Valid",
            value: "false",
          },
          {
            label: "Invalid",
            value: "true",
          },
        ],
      },
    ],
  },
];

let cached_customers: Customer[] | undefined;
let cached_stores: Map<string, CompanyStore> | undefined;

function parseSubmitIds(raw: string): string[] {
  const seen = new Set<string>();
  const ids: string[] = [];
  for (const line of raw.split(/\r\n|\n|\r/)) {
    const id = line.trim().replace(/^["']+|["']+$/g, "").trim();
    if (!id || seen.has(id)) {
      continue;
    }
    seen.add(id);
    ids.push(id);
  }
  return ids;
}

function BulkDeleteSubmitsForm(props: { companyId: string; onDeleted: () => void }) {
  const [text, setText] = useState("");
  const [running, setRunning] = useState(false);
  const [progress, setProgress] = useState<{
    success: number;
    failed: number;
    total: number;
    failures: { id: string; reason: string }[];
  } | null>(null);

  const onDelete = async () => {
    const ids = parseSubmitIds(text);
    if (ids.length === 0) {
      window.webApp.showMessage("warning", "No submit ids found. Paste one id per line.");
      return;
    }
    setRunning(true);
    window.webApp.setDialog({ okDisabled: true });
    let success = 0;
    let failed = 0;
    const failures: { id: string; reason: string }[] = [];
    setProgress({ success: 0, failed: 0, total: ids.length, failures: [] });
    try {
      for (const submitId of ids) {
        try {
          await deleteSurveyEventSubmit(submitId, props.companyId, true);
          success += 1;
        } catch (error: any) {
          failed += 1;
          failures.push({ id: submitId, reason: error?.message || String(error) });
        }
        setProgress({ success, failed, total: ids.length, failures: [...failures] });
      }
      if (success > 0) {
        props.onDeleted();
      }
    } finally {
      setRunning(false);
      window.webApp.setDialog({ okDisabled: false });
    }
  };

  return (
    <Stack spacing={2}>
      <Typography variant="body2">
        Paste submit IDs, one per line. Once deleted, records cannot be recovered.
      </Typography>
      <TextField
        multiline
        minRows={8}
        label="Submit IDs"
        InputLabelProps={{ shrink: true }}
        placeholder={"id-1\nid-2\nid-3"}
        value={text}
        onChange={(e) => setText(e.target.value)}
        disabled={running}
        fullWidth
      />
      <LoadingButton onClick={onDelete} disabled={running || !text.trim()}>
        Delete
      </LoadingButton>
      {progress && (
        <Box sx={{ bgcolor: "action.hover", borderRadius: 1, px: 2, py: 1.5 }}>
          <Stack spacing={1}>
            {[
              { label: "Progress", value: `${progress.success + progress.failed} / ${progress.total}` },
              { label: "Successfully deleted", value: progress.success, color: "success.main" },
              { label: "Failed", value: progress.failed, color: progress.failed > 0 ? "error.main" : undefined },
            ].map((row) => (
              <Stack key={row.label} direction="row" justifyContent="space-between" alignItems="baseline">
                <Typography variant="body2" color="text.secondary">
                  {row.label}
                </Typography>
                <Typography variant="body2" fontWeight={600} color={row.color}>
                  {row.value}
                </Typography>
              </Stack>
            ))}
            {progress.failures.length > 0 && (
              <Box pt={0.5}>
                <Typography variant="body2" color="text.secondary" mb={0.5}>
                  Failed IDs
                </Typography>
                <Box sx={{ maxHeight: 160, overflow: "auto" }}>
                  {progress.failures.map(({ id, reason }) => (
                    <Box key={id} mb={0.75}>
                      <Typography variant="body2" sx={{ fontFamily: "monospace", wordBreak: "break-all" }}>
                        {id}
                      </Typography>
                      <Typography variant="caption" color="error.main">
                        {reason}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Box>
            )}
          </Stack>
        </Box>
      )}
    </Stack>
  );
}

export default forwardRef((props: { id: string; customers?: SurveyEventSubmit[]; companyId: string }, ref) => {
  const { id, companyId } = props;
  const tableRef = useRef<any>(null);

  useEffect(() => {
    load();
    return () => {
      cached_customers = undefined;
      cached_stores = undefined;
    };
  }, []);

  useImperativeHandle(ref, () => ({
    onSubmit() {
      return Promise.resolve([]);
    },
  }));

  const load = async () => {
    const [customers, stores] = await Promise.all([
      getCustomers(),
      getCompanyStoresByCompanyId(companyId, {
        isAll: true,
        attributes: ["pk", "name", "storeId"],
      }),
    ]);
    cached_customers = customers.data;
    cached_stores = stores.data.reduce((map, store) => {
      map.set(store.id, store);
      return map;
    }, new Map<string, CompanyStore>());
  };

  const loadData = async (param: Pagination) => {
    if (param.filter?.and) {
      param.filter.and = param.filter.and.map((condition) => {
        if (condition.key === "invalid") {
          return {
            ...condition,
            values: condition.values.map((v) => v === "true"),
          };
        }
        return condition;
      });
    }
    const res: any = await getSurveyEventSubmitsById({
      id,
      attributes: ["pk", "sk", "data", "uid", "name", "device", "status", "viewedAt", "answeredAt", "sentAt", "invalid", "invalidReason"],
      ...param,
    });
    return res;
  };

  const onDelete = async (record: SurveyEventSubmit) => {
    await deleteSurveyEventSubmit(record.id, record.companyId);
  };

  const loadAllSubmits = async () => {
    let offset = undefined;
    let data: SurveyEventSubmit[] = [];
    do {
      const res: any = await getSurveyEventSubmitsById({
        id,
        offset,
        limit: 500,
      });
      data = data.concat(res.data);
      offset = res.offset;
    } while (offset);
    return data;
  };

  const downloadAllRecords = async () => {
    openWindowInAsync(async () => {
      const url = await getAllRecordsUrl({ id, companyId });
      return url;
    }, "Downloading records..., please do not close this window.");
  };

  return (
    <div>
      <Stack direction={"row"}>
        <LoadingButton
          sx={{ marginRight: 2 }}
          onClick={async () => {
            const allData = await loadAllSubmits();
            const data = await sendSurveyEventEmails({
              id,
              customerEmails: allData
                .filter((a) => a.status === SurveyEventStatusEnum.draft && a.email)
                .map((a) => ({
                  email: a.email!,
                  template: SurveyEventEmailTemplateEnum.send,
                })),
            });
            tableRef?.current?.onRefresh?.(data);
          }}
        >
          Send Email To All Draft Status
        </LoadingButton>
        <LoadingButton onClick={downloadAllRecords}>Download All Records</LoadingButton>
        <LoadingButton
          wrapperSx={{ marginLeft: "auto" }}
          onClick={async () => {
            await window.webApp.toggleDialog({
              title: "Delete Submits",
              element: <BulkDeleteSubmitsForm companyId={companyId} onDeleted={() => tableRef?.current?.onRefresh?.()} />,
              okText: "Close",
              noCancel: true,
              minWidth: 560,
              onOk: async () => {},
              open: true,
            });
          }}
        >
          Delete
        </LoadingButton>
      </Stack>
      <PaginatedTableList<SurveyEventSubmit>
        headCells={headCells}
        ref={tableRef}
        uid="uid"
        addButtonClassName="survey-customer-add-btn"
        onLoad={loadData}
        filters={filters}
        initialSortKey={"answeredAt"}
        initialOrder={"desc"}
        onDelete={onDelete}
        searchKeys={["id"]}
        searchText="Search by full submit id"
        searchFilterType={PaginationFilterType.equal}
      />
    </div>
  );
});
